import { useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import { IconoCheck, IconoCerrar } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import { useMarcarGastoPagado } from '../../lib/mutaciones';
import type { Gasto } from '../../lib/consultas';

interface Props {
  gasto: Gasto;
  alCerrar: () => void;
  alPagarExitoso?: () => void;
}

export function ModalPagarGasto({ gasto, alCerrar, alPagarExitoso }: Props) {
  const avisos = useAvisos();
  const marcarPagadoMut = useMarcarGastoPagado();

  const [fechaPago, setFechaPago] = useState<string>(new Date().toISOString().slice(0, 10));
  const [medioPago, setMedioPago] = useState<string>('TRANSFERENCIA');
  const [referencia, setReferencia] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await marcarPagadoMut.mutateAsync({
        id: gasto.id,
        datos: {
          fechaPago,
          medioPago: medioPago as any,
          referencia: referencia.trim() || undefined,
        },
      });

      avisos.exito(`Gasto "${gasto.concepto}" marcado como pagado exitosamente.`);
      alPagarExitoso?.();
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo marcar el gasto como pagado.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 480, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              Marcar Compromiso como Pagado
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Registra el egreso efectivo de tesorería y salda la cuenta por pagar.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '16px 24px' }}>
          {/* Tarjeta resumen del gasto */}
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{gasto.concepto}</span>
              <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>
                {formatearCLP(gasto.montoTotal || gasto.montoNeto)}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Proveedor: <strong>{gasto.proveedor?.nombre ?? gasto.proveedorNombre ?? 'Sin proveedor'}</strong>
              {gasto.numeroDocumento && ` · Folio: #${gasto.numeroDocumento}`}
            </div>
            {gasto.proyecto && (
              <div style={{ fontSize: 11, color: 'var(--accent-blue)' }}>
                Imputado a: {gasto.proyecto.codigo} — {gasto.proyecto.nombre}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="pago_fecha">Fecha de Pago Efectivo</label>
              <input
                id="pago_fecha"
                type="date"
                value={fechaPago}
                onChange={(e) => setFechaPago(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="pago_medio">Medio de Pago</label>
              <select id="pago_medio" value={medioPago} onChange={(e) => setMedioPago(e.target.value)}>
                <option value="TRANSFERENCIA">Transferencia Bancaria Electrónica</option>
                <option value="CHEQUE">Cheque al Día / Plazo</option>
                <option value="TARJETA_CREDITO">Tarjeta de Crédito Empresa</option>
                <option value="TARJETA_DEBITO">Tarjeta de Débito / Redcompra</option>
                <option value="EFECTIVO">Caja Chica / Efectivo</option>
                <option value="OTRO">Otro Medio</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="pago_ref">N° Comprobante / Referencia Transferencia</label>
              <input
                id="pago_ref"
                type="text"
                placeholder="Ej: Transferencia Santander #998124"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={marcarPagadoMut.isPending}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={marcarPagadoMut.isPending}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <IconoCheck size={15} />
              <span>{marcarPagadoMut.isPending ? 'Confirmando…' : 'Confirmar Pago'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
